import React, { useState } from 'react';
import { ParsedUrlQuery } from 'querystring';
import Link from 'next/link';
import Lottie from 'react-lottie';
import { useRouter } from 'next/router';
import Icon from '@common/assets';
import { useUserInfo } from '@repository/auth/useUserInfo';
import asterisk from '@graphic/asterisk.json';
import codeBar from '@graphic/codeBar.json';
import thanSign from '@graphic/thanSign.json';
import Colors from '@domain/constant/colors';
import { RouteUrl } from '@utils/routeUtil';
import { isEmpty } from '@utils/common';
import classnames from 'classnames/bind';
import styles from './serviceList.module.scss';
import { usePACCApplyStatus } from '@repository/phs/pacc/apply/usePhsPaccApply';
import { usePSTApplyStatus } from '@repository/phs/pst/apply/usePhsPstApply';

const cx = classnames.bind(styles);

const PHS_TAB_ID = 'phsTest';

const visual1Options = {
  loop: true,
  autoplay: false,
  animationData: asterisk,
};

const visual2Options = {
  loop: true,
  autoplay: false,
  animationData: codeBar,
};

const visual3Options = {
  loop: true,
  autoplay: false,
  animationData: thanSign,
};

const openWindowTab = (url: string, query?: ParsedUrlQuery) => {
  const nextUrl = isEmpty(query) ? url : `${url}?${new URLSearchParams(query as Record<string, string>).toString()}`;
  window.open(nextUrl, PHS_TAB_ID);
};

const ServiceList = () => {
  const { query } = useRouter();
  const { data: userInfo } = useUserInfo();
  const isLogin = !!userInfo?.id;

  const { data: paccApplyStatusData } = usePACCApplyStatus(isLogin);
  const { data: pstApplyStatusData } = usePSTApplyStatus(isLogin);

  const paccChanceCount = paccApplyStatusData?.chanceCount ?? 5;
  const pstChanceCount = pstApplyStatusData?.chanceCount ?? 2;

  // [D] UI 확인용(카드 호버 테스트)
  const [service1IsHover, setService1IsHover] = useState(false);
  const [service2IsHover, setService2IsHover] = useState(false);
  const [service3IsHover, setService3IsHover] = useState(false);

  return (
    <div className={cx('serviceListArea')}>
      {/*개발자 역검 */}
      <a
        className={cx('service', 'dev_enter_acca')}
        role="button"
        onMouseEnter={() => setService1IsHover(true)}
        onMouseLeave={() => setService1IsHover(false)}
        data-testid="phs_pacc_test"
        onClick={() => openWindowTab(RouteUrl.PHS_PACC_TEST_GATE, query)}
      >
        <div className={cx('serviceText')}>실전 응시 및 튜토리얼 제공</div>
        <div className={cx('serviceTitleArea')}>
          <strong className={cx('serviceTitle')}>개발자 역검</strong>
          <div className={cx('infoArea')}>
            <div className={cx('btnInfo')}>
              <Icon name="informationLight" width={20} height={20} fill={Colors.C_COOL_GRAY_60} />
            </div>
            <div className={cx('tooltipArea')}>
              <Icon name="tooltipTopTail" width={8} height={8} fill={Colors.C_COOL_GRAY_70} />
              <div className={cx('tooltip')}>
                <em className={cx('tooltipTitle')}>개발자 역검이란?</em>
                <p className={cx('tooltipDesc')}>
                  간단한 게임과 성향파악 검사를 통해
                  <br />
                  개발자에게 필요한 핵심 역량을 진단합니다.
                </p>
              </div>
            </div>
          </div>
        </div>
        <div className={cx('serviceCount')}>
          이번달 잔여 횟수 : <span className={cx('emphasis')}>{paccChanceCount}회</span>
        </div>
        <div className={cx('serviceDescArea')}>
          <ul className={cx('serviceDescList')}>
            <li className={cx('listItem')}>
              <Icon name="checkLight" width={24} height={24} fill={Colors.C_JOBDA_WHITE} />월 5회 응시 가능
            </li>
            <li className={cx('listItem')}>
              <Icon name="checkLight" width={24} height={24} fill={Colors.C_JOBDA_WHITE} />
              무제한 연습 기회 제공
            </li>
            <li className={cx('listItem')}>
              <Icon name="checkLight" width={24} height={24} fill={Colors.C_JOBDA_WHITE} />
              역검 결과표 제공
            </li>
          </ul>

          <div className={cx('visualWrap')}>
            <Lottie options={visual1Options} isStopped={!service1IsHover} />
          </div>
        </div>
      </a>
      {/*개발자 구현 능력 검사 */}
      <a
        className={cx('service', 'dev_enter_phs')}
        role="button"
        onMouseEnter={() => setService2IsHover(true)}
        onMouseLeave={() => setService2IsHover(false)}
        data-testid="phs_pst_test"
        onClick={() => openWindowTab(RouteUrl.PHS_PST_TEST_GATE, query)}
      >
        <div className={cx('serviceText')}>실전 응시 및 튜토리얼 제공</div>
        <div className={cx('serviceTitleArea')}>
          <strong className={cx('serviceTitle')}>개발 구현 능력 검사</strong>
          <div className={cx('infoArea')}>
            <div className={cx('btnInfo')}>
              <Icon name="informationLight" width={20} height={20} fill={Colors.C_COOL_GRAY_60} />
            </div>
            <div className={cx('tooltipArea')}>
              <Icon name="tooltipTopTail" width={8} height={8} fill={Colors.C_COOL_GRAY_70} />
              <div className={cx('tooltip')}>
                <em className={cx('tooltipTitle')}>개발 구현 능력 검사란?</em>
                <p className={cx('tooltipDesc')}>
                  단계별로 제시되는 추가 조건을 해결하며
                  <br />
                  요구사항 분석과 확장적 사고 능력을 진단합니다.
                </p>
              </div>
            </div>
          </div>
        </div>
        <div className={cx('serviceCount')}>
          이번달 잔여 횟수 : <span className={cx('emphasis')}>{pstChanceCount}회</span>
        </div>
        <div className={cx('serviceDescArea')}>
          <ul className={cx('serviceDescList')}>
            <li className={cx('listItem')}>
              <Icon name="checkLight" width={24} height={24} fill={Colors.C_JOBDA_WHITE} />월 2회 응시 가능
            </li>
            <li className={cx('listItem')}>
              <Icon name="checkLight" width={24} height={24} fill={Colors.C_JOBDA_WHITE} />
              무제한 연습 기회 제공
            </li>
            <li className={cx('listItem')}>
              <Icon name="checkLight" width={24} height={24} fill={Colors.C_JOBDA_WHITE} />
              검사 결과표 제공
            </li>
          </ul>

          <div className={cx('visualWrap')}>
            <Lottie options={visual2Options} isStopped={!service2IsHover} />
          </div>
        </div>
      </a>

      <Link href={RouteUrl.PHS_PACC_RESULTS} legacyBehavior>
        <a
          className={cx('service', 'dev_enter_report')}
          role="button"
          onMouseEnter={() => setService3IsHover(true)}
          onMouseLeave={() => setService3IsHover(false)}
          data-testid="phs_pacc_result"
        >
          <div className={cx('serviceText')}>역량 프로파일 및 취업 가이드</div>
          <div className={cx('serviceTitleArea')}>
            <strong className={cx('serviceTitle')}>결과표 확인</strong>
          </div>
          <div className={cx('serviceDescArea')}>
            <ul className={cx('serviceDescList')}>
              <li className={cx('listItem')}>
                <Icon name="checkLight" width={24} height={24} fill={Colors.C_JOBDA_WHITE} />
                응시 점검 결과
              </li>
              <li className={cx('listItem')}>
                <Icon name="checkLight" width={24} height={24} fill={Colors.C_JOBDA_WHITE} />
                심층 분석 결과
              </li>
              <li className={cx('listItem')}>
                <Icon name="checkLight" width={24} height={24} fill={Colors.C_JOBDA_WHITE} />
                결과표 활용 가능
              </li>
            </ul>

            <div className={cx('visualWrap')}>
              <Lottie options={visual3Options} isStopped={!service3IsHover} />
            </div>
          </div>
        </a>
      </Link>
    </div>
  );
};

const EventServiceList = () => {
  const { query } = useRouter();
  const { data: userInfo } = useUserInfo();
  const isLogin = !!userInfo?.id;

  const { data: paccApplyStatusData } = usePACCApplyStatus(isLogin);
  const { data: pstApplyStatusData } = usePSTApplyStatus(isLogin);

  const paccChanceCount = paccApplyStatusData?.chanceCount ?? 5;
  const pstChanceCount = pstApplyStatusData?.chanceCount ?? 2;

  // [D] UI 확인용(카드 호버 테스트)
  const [service1IsHover, setService1IsHover] = useState(false);
  const [service2IsHover, setService2IsHover] = useState(false);
  const [service3IsHover, setService3IsHover] = useState(false);

  return (
    <div className={cx('serviceListArea')}>
      {/*개발자 역검 */}
      <a
        className={cx('service', 'dchamps', 'dev_enter_acca')}
        role="button"
        onMouseEnter={() => setService1IsHover(true)}
        onMouseLeave={() => setService1IsHover(false)}
        data-testid="phs_pacc_test"
        onClick={() => openWindowTab(RouteUrl.PHS_PACC_TEST_GATE, query)}
      >
        <div className={cx('badge')}>디챔스 참가하기</div>
        <div className={cx('serviceTitleArea', 'dchamps')}>
          <strong className={cx('serviceTitle')}>개발자 역검</strong>
          <strong className={cx('testJoin')}>응시하기</strong>
        </div>
        <div className={cx('serviceCount')}>
          역량검사 <span className={cx('divider')}>|</span> 이번달 잔여 횟수 : {paccChanceCount}
        </div>
        <div className={cx('serviceDescArea')}>
          <ul className={cx('serviceDescList')}>
            <li className={cx('listItem')}>
              <Icon name="checkLight" width={24} height={24} fill={Colors.C_JOBDA_WHITE} />월 5회 응시 가능
            </li>
            <li className={cx('listItem')}>
              <Icon name="checkLight" width={24} height={24} fill={Colors.C_JOBDA_WHITE} />
              무제한 연습 기회 제공
            </li>
            <li className={cx('listItem')}>
              <Icon name="checkLight" width={24} height={24} fill={Colors.C_JOBDA_WHITE} />
              역검 결과표 제공
            </li>
          </ul>

          <div className={cx('visualWrap')}>
            <Lottie options={visual1Options} isStopped={!service1IsHover} />
          </div>
        </div>
      </a>
      {/*개발자 구현 능력 검사 */}
      <a
        className={cx('service', 'dchamps', 'dev_enter_phs')}
        role="button"
        onMouseEnter={() => setService2IsHover(true)}
        onMouseLeave={() => setService2IsHover(false)}
        data-testid="phs_pst_test"
        onClick={() => openWindowTab(RouteUrl.PHS_PST_TEST_GATE, query)}
      >
        <div className={cx('badge')}>디챔스 참가하기</div>
        <div className={cx('serviceTitleArea', 'dchamps')}>
          <strong className={cx('serviceTitle')}>개발 구현 능력 검사</strong>
          <strong className={cx('testJoin')}>응시하기</strong>
        </div>
        <div className={cx('serviceCount')}>
          코딩테스트 <span className={cx('divider')}>|</span> 이번달 잔여 횟수 : {pstChanceCount}
        </div>
        <div className={cx('serviceDescArea')}>
          <ul className={cx('serviceDescList')}>
            <li className={cx('listItem')}>
              <Icon name="checkLight" width={24} height={24} fill={Colors.C_JOBDA_WHITE} />월 2회 응시 가능
            </li>
            <li className={cx('listItem')}>
              <Icon name="checkLight" width={24} height={24} fill={Colors.C_JOBDA_WHITE} />
              무제한 연습 기회 제공
            </li>
            <li className={cx('listItem')}>
              <Icon name="checkLight" width={24} height={24} fill={Colors.C_JOBDA_WHITE} />
              검사 결과표 제공
            </li>
          </ul>

          <div className={cx('visualWrap')}>
            <Lottie options={visual2Options} isStopped={!service2IsHover} />
          </div>
        </div>
      </a>

      <Link href={RouteUrl.PHS_DCHAMPS_RESULTS} legacyBehavior>
        <a
          className={cx('service', 'dev_enter_report')}
          role="button"
          onMouseEnter={() => setService3IsHover(true)}
          onMouseLeave={() => setService3IsHover(false)}
          data-testid="phs_pacc_result"
        >
          <div className={cx('serviceText')}>역량 프로파일 및 취업 가이드</div>
          <div className={cx('serviceTitleArea')}>
            <strong className={cx('serviceTitle')}>결과표 확인</strong>
          </div>
          <div className={cx('serviceDescArea')}>
            <ul className={cx('serviceDescList')}>
              <li className={cx('listItem')}>
                <Icon name="checkLight" width={24} height={24} fill={Colors.C_JOBDA_WHITE} />
                응시 점검 결과
              </li>
              <li className={cx('listItem')}>
                <Icon name="checkLight" width={24} height={24} fill={Colors.C_JOBDA_WHITE} />
                심층 분석 결과
              </li>
              <li className={cx('listItem')}>
                <Icon name="checkLight" width={24} height={24} fill={Colors.C_JOBDA_WHITE} />
                결과표 활용 가능
              </li>
            </ul>

            <div className={cx('visualWrap')}>
              <Lottie options={visual3Options} isStopped={!service3IsHover} />
            </div>
          </div>
        </a>
      </Link>
    </div>
  );
};

export { ServiceList, EventServiceList };
